import { normalizePositionDetails, positionDetailsErrors, positionBenefitOptions, BASE_BENEFIT_OPTIONS, agesServed } from "./positionDetails";
import { typeLabel } from "../theme";
/**
 * Posting semantics shared by the wizard, persistence, and discovery.
 *
 * A listing is either a client CASE or a POSITION (a role the agency is staffing).
 * The credential being hired is the listing's `type` (cases.type); the kind says what
 * the listing IS. Which credentials may be hired as a position is configured here
 * (POSITION_TYPES) and mirrored in the database by public.position_type_allowed().
 * BCBA and RBT today; widening it again, for example to BCaBA, is those two edits, not a rename.
 */
export const CASE_KIND = "case";
export const POSITION_KIND = "position";
/** Credentials an agency may hire as a position. Keep in sync with position_type_allowed(). */
export const POSITION_TYPES = ["BCBA", "RBT"];

export function isPosition(listing) {
  return (listing?.listingKind || listing?.listing_kind) === POSITION_KIND;
}

/** True when this credential type may be posted as a position. */
export function positionTypeAllowed(type) {
  return POSITION_TYPES.includes(type);
}

/** "BCBA position" / "Puesto de BCBA": the badge/label for a position, by its credential. */
export function positionLabel(t, listing) {
  return t("position.label", { type: typeLabel(listing?.type || listing?.role) || "" });
}

/**
 * Detail-view identity for a position: the job title as the headline (falling back to
 * the "BCBA position" label) and a "BCBA · Part-time · 1099" subline. A commitment that
 * is still to be determined is left out of the subline rather than shown as a value.
 */
export function positionHeadline(t, listing) {
  const d = listing?.positionDetails || {};
  const typed = String(d.jobTitle || "").trim();
  const title = typed || positionLabel(t, listing);
  const employments = listing?.employments || (listing?.employment ? [listing.employment] : []);
  const commitment = d.timeCommitment && d.timeCommitment !== "to_be_determined" ? t(`position.commitment.${d.timeCommitment}`) : "";
  const subline = [typeLabel(listing?.type || listing?.role) || "", commitment, employments.join(" / ")].filter(Boolean).join(" · ");
  return { title, subline, titleIsFallback: !typed };
}

/**
 * The agency-private short name of a listing: client initials for a case, the position
 * title for a position (a position has no client). "" when there is none.
 */
export function listingIdent(listing) {
  if (isPosition(listing)) return String(listing?.positionDetails?.jobTitle || "").trim().slice(0, 40);
  return String(listing?.clientInitials || "").trim();
}

function positionTypeError() {
  return `Agency positions are currently available only for ${POSITION_TYPES.map(typeLabel).join(" and ")} openings.`;
}

/**
 * Ages served: the age span a position's program works with (a program attribute, never a client age).
 * Both bounds or neither; whole years from 1 to 99 with min <= max. Anything else becomes null/null.
 */
export function normalizeAgesServed(min, max) {
  const lo = min == null || String(min).trim() === "" ? null : Number(min);
  const hi = max == null || String(max).trim() === "" ? null : Number(max);
  const ok = Number.isInteger(lo) && Number.isInteger(hi) && lo >= 1 && hi <= 99 && hi >= lo;
  if (!ok) return { ageMin: null, ageMax: null };
  // A single age is always widened to a range (plus or minus one), the same way a client
  // case's age is stored, so no listing ever shows one exact age.
  if (lo === hi) return { ageMin: Math.max(1, lo - 1), ageMax: Math.min(99, hi + 1) };
  return { ageMin: lo, ageMax: hi };
}

/** True for a case (which always shows its client age range) and for a position that carries ages served. */
export function showsAgeRange(listing) {
  return !isPosition(listing) || agesServed(listing) != null;
}

/**
 * Ages served for the form. Strict (the default, used when a listing is persisted) is all-or-nothing.
 * `keep` (used while the form is being edited, switched or resumed) preserves a half-typed or invalid
 * pair exactly as entered, so a role change or a draft resume never silently drops it; validation
 * (positionSubmitErrors) still refuses it at submit.
 */
function agesForForm(min, max, keep) {
  const strict = normalizeAgesServed(min, max);
  if (strict.ageMin != null || !keep) return strict;
  return { ageMin: min ?? null, ageMax: max ?? null };
}

// Positions must never carry a client profile or client authorizations, even
// when populated from an old draft, a duplicate, or a case-to-position toggle.
export function normalizeListingForm(form, { keepPartialAges = false } = {}) {
  const listingKind = form.listingKind || CASE_KIND;
  if (![CASE_KIND, POSITION_KIND].includes(listingKind)) {
    throw new Error("Unsupported opening type.");
  }
  // An empty role is a position whose role has not been chosen yet (the wizard asks first); it is
  // refused at validation, never published. A chosen role must be an allowed credential.
  if (listingKind === POSITION_KIND && form.role && !positionTypeAllowed(form.role)) {
    throw new Error(positionTypeError());
  }
  if (listingKind === CASE_KIND) return { ...form, listingKind, positionDetails: null };
  const rbt = form.role === "RBT";
  return {
    ...form, listingKind,
    clientInitials: "", age: "", ...agesForForm(form.ageMin, form.ageMax, keepPartialAges),
    clientSex: "", therapistSexPref: "", diagnosisTypes: [], diagnosisOther: "",
    urgency: "",
    payerTypes: [], languages: [], sessionTypes: [], sessionHours: {},
    mustHaves: null, mustHaveSkills: [], // Case Fit must haves are case only for now
    assessmentTotalHours: "", serviceTime: "",
    positionDetails: normalizePositionDetails(form.positionDetails, form),
    bcbaSupport: false, supervision: '',
    // An RBT position is always in person and carries none of the BCBA workload fields.
    ...(rbt ? { deliveryMode: "in_person", caseloadSize: "", billableHrs: "", rbtCount: "" } : {}),
    // Perks are limited to what the chosen role and employment type offer on screen, so a perk that is
    // hidden (a BCBA-only perk after switching to RBT, an RBT-only one after switching back, any W2 perk
    // on a 1099 position) never publishes invisibly. "BCBA Fieldwork Hours" is fieldwork offered TO a
    // trainee: a perk of an RBT position only. Until a role is chosen nothing is filtered.
    benefits: form.role ? (form.benefits || []).filter(b => positionBenefitOptions(form.role, form.employments, BASE_BENEFIT_OPTIONS).includes(b)) : (form.benefits || []),
  };
}

/**
 * Submit-time validation for a position: the normalized form, plus the ages exactly as the agency
 * entered them (normalization would otherwise drop a half-entered pair before it could be refused).
 */
export function positionSubmitErrors(raw, normalized) {
  return positionValidationErrors(isPosition(normalized) ? { ...normalized, ageMin: raw?.ageMin, ageMax: raw?.ageMax } : normalized);
}

export function positionValidationErrors(form, section = "all") {
  if (!isPosition(form)) return [];
  const errors = positionDetailsErrors(form, section);
  if (!positionTypeAllowed(form.role)) errors.push(form.role ? positionTypeError() : "Choose the role you are hiring for.");
  if (['all', 'identity'].includes(section) && !form.notes?.trim()) {
    errors.push(`Describe the responsibilities for this ${typeLabel(form.role) || "new"} position.`);
  }
  if (["all", "schedule"].includes(section) && form.hours !== "" && form.hours != null && (!Number.isFinite(Number(form.hours)) || Number(form.hours) <= 0)) {
    errors.push("Expected weekly hours must be positive, or leave blank if to be determined.");
  }
  if (["all", "schedule"].includes(section)) {
    const lo = form.ageMin, hi = form.ageMax;
    const blank = v => v == null || String(v).trim() === "";
    if (!(blank(lo) && blank(hi)) && normalizeAgesServed(lo, hi).ageMin == null) {
      errors.push("Ages served need a youngest and an oldest age (whole years, 1 to 99, youngest not above oldest), or leave both blank.");
    }
  }
  return errors;
}
